/* eslint-disable no-labels */
/* eslint-disable no-console */
/* eslint-disable no-lone-blocks */
import { motion } from 'framer-motion';
import { ReactElement, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button, Loader, NavBar, PlayerList } from '@/components';
import { StepWizard } from '@/components/StepWizard';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import useToast from '@/hooks/useToast';
import { useTournament } from '@/hooks/useTournament';
import ROUTES from '@/router/routes';
import { getCriteriaRecommender, saveTeamDraft } from '@/services/buildTeam11';
import { TemplatePlayerWithExtra } from '@/types';
import { ArrayInfoCriterioTO, PlayerRecommender } from '@/types/recommender';
import {
  DraftTeam,
  PlayerClub,
  RequestEquipoBorrador,
  TemplatePlayer,
} from '@/types/team';
import {
  CRITERIA_RECOMMENDER,
  PLAYER_NAME_POSITION_BY_ID,
  POSITIONS_LABELS,
  FADE_IN_OUT_LEFT,
  scrollSmooth,
} from '@/utils';
import {
  PLAYER_NAME_SINGULAR_POSITION_BY_ID,
  PLAYER_POSITION_BY_ID,
  TACTICS_ARR,
  TEXTS,
} from '@/utils/constants';
import { TacticDescriptor } from '@/utils/tacticUtils';

interface TypeState {
  step: number;
  playerPosition: number;
  criteria: number;
  headlines: PlayerRecommender[] | PlayerRecommender;
  substitute: PlayerRecommender;
  idTactic: number;
  playersDrafter: PlayerClub[] | [];
  budget: number;
  idUser: number;
}

const initialValueSubstitute: PlayerRecommender = {
  id: 0,
  nombre: '',
  club: '',
  posicion: 'ARQ',
  cotizacion: 0,
  status: 0,
};

interface Props {
  onSubmitRecommender?: (team: DraftTeam) => void;
}

export const Recommender = ({ onSubmitRecommender }: Props) => {
  const title = 'Recomendador';
  const { userInitialData } = useAuth();
  const { isDesktop, isMobile } = useIsDesktop();
  const { tournamentMap } = useTournament();
  const playersMaps = usePlayerTemplateMap();
  const { state } = useTeamStore();
  const toast = useToast();
  const navigate = useNavigate();
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const [stateRecommend, setStateRecommend] = useState<TypeState>({
    step: 0,
    playerPosition: 1,
    criteria: 0,
    headlines: [],
    substitute: initialValueSubstitute,
    idTactic: 1,
    playersDrafter: [],
    budget: 0,
    idUser: 0,
  });

  // ONBTENER CRITERIOS DE RECOMENDADOR
  const { data: dataCriteria, isLoading } = useQuery<ArrayInfoCriterioTO[]>(
    'getCriteriaRecommender',
    async () => await getCriteriaRecommender(),
    {
      refetchOnWindowFocus: false,
    }
  );

  // GUARDAR EQUIPO BORRADOR
  const { mutateAsync: submitDraftTeam, isLoading: isLoadingDraftOnSubmit } =
    useMutation((newTeamDraft: RequestEquipoBorrador) => {
      return saveTeamDraft(newTeamDraft);
    });

  useEffect(() => {
    const timer: any = setTimeout(() => {
      scrollSmooth();
      setStateRecommend({
        ...stateRecommend,
        idTactic: state.tactic?.id || 1,
        playersDrafter: (state.teamDraft as PlayerClub[]) || [],
        idUser: userInitialData ? userInitialData?.idUsuarioDT : 0,
        budget: state.balance || 0,
      });
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (stateRecommend.step === 1) {
      setStateRecommend({
        ...stateRecommend,
        headlines: [],
        substitute: initialValueSubstitute,
      });
    }

    const tacticArray: number[] = TACTICS_ARR[stateRecommend.idTactic];

    if (dataCriteria) {
      if (
        dataCriteria[stateRecommend.criteria].id ===
        stateRecommend.criteria + 1
      ) {
        let dataHeadlines: PlayerRecommender[] | PlayerRecommender;
        if (stateRecommend.playerPosition === 1) {
          dataHeadlines = dataCriteria[stateRecommend.criteria].jugadores.slice(
            0,
            1
          )[0];
        } else {
          dataHeadlines = dataCriteria[stateRecommend.criteria].jugadores.slice(
            0,
            tacticArray[stateRecommend.playerPosition - 2]
          );
        }
        const dataSubstitute =
          dataCriteria[stateRecommend.criteria]?.jugadores?.slice(-1)[0];

        if (!dataHeadlines && stateRecommend.step === 2) {
          toast.show(
            `No hay datos para el criterio seleccionado: ${
              Object.values(getCriteriaByPosition()[stateRecommend.criteria])[0]
            }.`,
            true
          );
          setStateRecommend({
            ...stateRecommend,
            step: 1,
          });
        } else {
          setStateRecommend({
            ...stateRecommend,
            headlines: dataHeadlines,
            substitute: dataSubstitute,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stateRecommend.criteria,
    stateRecommend.playerPosition,
    stateRecommend.idTactic,
    dataCriteria,
    stateRecommend.step,
  ]);

  const getNamePositionById = (id: number) => {
    return PLAYER_NAME_POSITION_BY_ID[
      id as keyof typeof PLAYER_NAME_POSITION_BY_ID
    ];
  };

  const getCriteriaByPosition = () => {
    const position =
      PLAYER_POSITION_BY_ID[
        stateRecommend.playerPosition as keyof typeof PLAYER_POSITION_BY_ID
      ];

    return CRITERIA_RECOMMENDER[position as keyof typeof CRITERIA_RECOMMENDER];
  };

  const getIsChecked = () => {
    const tacticLine = new TacticDescriptor(state.tactic?.formation || []);
    const startLine = tacticLine.calcStartOfLine(stateRecommend.playerPosition);
    const endLine = tacticLine.calcEndOfLine(stateRecommend.playerPosition);

    if (stateRecommend.playerPosition === 1) {
      return stateRecommend?.playersDrafter
        .filter((p) => p.orden === 0 || p.orden === 11)
        .every((p) => p?.id === undefined);
    } else if (stateRecommend.playerPosition === 2) {
      return stateRecommend?.playersDrafter
        .filter((p) => {
          return (p.orden >= startLine && p.orden < endLine) || p.orden === 12;
        })
        .every((p) => p?.id === undefined);
    } else if (stateRecommend.playerPosition === 3) {
      return stateRecommend?.playersDrafter
        .filter((p) => {
          return (p.orden >= startLine && p.orden < endLine) || p.orden === 13;
        })
        .every((p) => p?.id === undefined);
    } else {
      return stateRecommend?.playersDrafter
        .filter((p) => {
          return (p.orden >= startLine && p.orden < endLine) || p.orden === 14;
        })
        .every((p) => p?.id === undefined);
    }
  };

  const renderPlayerOnList = (position: string, player: PlayerRecommender) => {
    const playerByPosition = position.includes('head')
      ? playersMaps.get(player?.id)
      : playersMaps.get(stateRecommend.substitute?.id);

    if (playerByPosition !== undefined) {
      const checked = getIsChecked();

      const statusInTournament = tournamentStatusMap
        ? tournamentStatusMap.get(playerByPosition.e)
        : undefined;

      const team = teamsMap ? teamsMap.get(playerByPosition.c) : undefined;

      if (checked) {
        handleInputChange({
          isChecked: checked,
          statePlayerId: playerByPosition.id,
          isSubstitute: position.includes('subs'),
        });
      }

      return (
        <PlayerList.Player
          key={playerByPosition.id}
          className="border-b border-gdt-gray-7"
          disabled={false}
          initialCheckState={checked}
          isSubstitute={position.includes('subs')}
          nameInput={`${playerByPosition.id}`}
          player={playerByPosition}
          team={team?.nombre ?? ''}
          teamId={team?.id ?? 0}
          tournamentStatus={statusInTournament}
          variant="selectable"
          onInputChanges={handleInputChange}
        />
      );
    }
  };

  const handleInputChange = (event: {
    isChecked: boolean;
    statePlayerId: number;
    isSubstitute: boolean;
  }) => {
    const player = playersMaps.get(event.statePlayerId);
    if (player) {
      addPlayersRecommended(player, event.isChecked, event.isSubstitute);
    }
  };

  let playersWillBeAdded: TemplatePlayerWithExtra[] = [];
  const addPlayersRecommended = (
    player: TemplatePlayer,
    isChecked: boolean,
    isSubstitute: boolean
  ) => {
    const noExistOnTeam =
      playersWillBeAdded.length === 0
        ? true
        : playersWillBeAdded.every((p) => p.id !== player.id);
    if (isChecked && noExistOnTeam) {
      if (isSubstitute) {
        playersWillBeAdded = [
          ...playersWillBeAdded,
          { ...player, isSubstitute },
        ];
      } else {
        playersWillBeAdded = [player, ...playersWillBeAdded];
      }
    } else {
      const newList = playersWillBeAdded.filter((p) => p.id !== player.id);
      playersWillBeAdded = newList;
    }
  };

  const createLinePositionsWithSubstitute = async (
    playersSelected: TemplatePlayerWithExtra[]
  ) => {
    const tacticLine = new TacticDescriptor(state.tactic?.formation || []);
    const sizeLine = tacticLine.calcLineSize(stateRecommend.playerPosition) + 1;
    const substitute: TemplatePlayerWithExtra[] = [];
    const headlines: TemplatePlayerWithExtra[] = [];

    for (let i = 0; i < sizeLine; i++) {
      if (!playersSelected[i]?.isSubstitute) {
        headlines.push({
          ...playersSelected[i],
          id: playersSelected[i]?.id,
        });
      } else {
        substitute.push({
          ...playersSelected[i],
          id: playersSelected[i]?.id,
        });
      }
    }

    return [...headlines, ...substitute];
  };

  const handleAddPlayer = async (
    playersSelected: TemplatePlayerWithExtra[]
  ) => {
    const newTeamWithRecommended = [...stateRecommend.playersDrafter];
    let substituteIsFull = false;
    let headlinesLineIsFull = false;
    let positionSelected = 0;

    const playersSelectedFullLine = await createLinePositionsWithSubstitute(
      playersSelected
    );

    for (let index = 0; index < playersSelectedFullLine.length; index++) {
      const {
        id: selectedPlayerId,
        p: positionPlayerSelected,
        isSubstitute,
      } = playersSelectedFullLine[index];
      positionSelected = positionPlayerSelected - 1;
      const positionIsEmptyByIndex = (counterPosition: number) => {
        if (newTeamWithRecommended[counterPosition].id === undefined) {
          return true;
        }

        return false;
      };

      const addPlayerOnList = (counterPosition: number) => {
        newTeamWithRecommended[counterPosition] = {
          ...newTeamWithRecommended[counterPosition],
          id: selectedPlayerId,
        };
      };

      const isFullTacticLine = (start: number, end: number) => {
        const newTeamSliced = [...newTeamWithRecommended];
        const tacticFormation = newTeamSliced.splice(start, end);
        return tacticFormation.every((player) => player.id !== undefined);
      };

      const setFullLineByPosition = () => {
        if (isSubstitute) {
          substituteIsFull = true;
        } else {
          headlinesLineIsFull = true;
        }
      };

      // Sumo arquero titular y suplente separado del resto de jugadores
      if (positionSelected === 0) {
        if (positionIsEmptyByIndex(0) && !isSubstitute) {
          addPlayerOnList(0);
        } else if (positionIsEmptyByIndex(11) && isSubstitute) {
          addPlayerOnList(11);
        } else {
          setFullLineByPosition();
        }
      } else {
        // Encontrar en que orden se va a agregar al usuario basado en la tactica;
        const tacticArray: number[] = TACTICS_ARR[stateRecommend.idTactic];

        // Modificar el objeto teamDraft directamente para agregar los jugadores
        // en la posicion correcta
        let position = 0;
        list: for (let i = 0; i <= tacticArray.length; i++) {
          const positionOnField: number = tacticArray[i];
          for (let j = 0; j < positionOnField; j++) {
            const counterPosition = ++position;
            if (
              tacticArray[0] === positionOnField &&
              i === 0 &&
              positionSelected === 1
            ) {
              // Sumar jugador defensas
              if (positionIsEmptyByIndex(counterPosition) && !isSubstitute) {
                addPlayerOnList(counterPosition);
                break list;
              } else if (isFullTacticLine(1, positionOnField) || isSubstitute) {
                if (positionIsEmptyByIndex(12) && isSubstitute) {
                  addPlayerOnList(12);
                  break list;
                } else {
                  setFullLineByPosition();
                }
              }
            } else if (
              tacticArray[1] === positionOnField &&
              i === 1 &&
              positionSelected === 2
            ) {
              // Sumar jugadores volantes
              if (positionIsEmptyByIndex(counterPosition) && !isSubstitute) {
                addPlayerOnList(counterPosition);
                break list;
              } else if (
                isFullTacticLine(tacticArray[0] + 1, positionOnField) ||
                isSubstitute
              ) {
                if (positionIsEmptyByIndex(13) && isSubstitute) {
                  addPlayerOnList(13);
                  break list;
                } else {
                  setFullLineByPosition();
                }
              }
            } else if (
              tacticArray[2] === positionOnField &&
              i === 2 &&
              positionSelected === 3
            ) {
              // Sumar jugador titular delanteros
              if (positionIsEmptyByIndex(counterPosition) && !isSubstitute) {
                addPlayerOnList(counterPosition);
                break list;
              } else if (
                isFullTacticLine(
                  tacticArray[0] + tacticArray[1] + 1,
                  positionOnField
                ) ||
                isSubstitute
              ) {
                if (positionIsEmptyByIndex(14) && isSubstitute) {
                  addPlayerOnList(14);
                  break list;
                } else {
                  setFullLineByPosition();
                }
              }
            }
          }
        }
      }
    }

    if (headlinesLineIsFull) {
      toast.show(TEXTS.team.maxHeadlinesAllow);
      return false;
    }

    if (substituteIsFull) {
      toast.show(TEXTS.team.maxSubstitutesAllow);
      return false;
    }

    const response = await submitDraftTeam({
      jugadores: newTeamWithRecommended,
      idTactica: stateRecommend.idTactic,
      idUsuario: stateRecommend.idUser,
    });

    if (response.estado) {
      if (response.equipoBorrador) {
        playersWillBeAdded = [];
        if (!headlinesLineIsFull || !substituteIsFull) {
          if (playersSelected.length === 1) {
            toast.show(
              `Agregaste el ${
                PLAYER_NAME_SINGULAR_POSITION_BY_ID[
                  stateRecommend.playerPosition as keyof typeof PLAYER_NAME_SINGULAR_POSITION_BY_ID
                ]
              } recomendado.`
            );
          } else {
            toast.show(
              `Agregaste los ${getNamePositionById(
                stateRecommend.playerPosition
              )} recomendados.`
            );
          }

          if (isMobile) {
            navigate(ROUTES.buildTeam11);
          } else {
            onSubmitRecommender &&
              onSubmitRecommender({
                id: response.equipoBorrador.id,
                idTactica: response.equipoBorrador.idTactica,
                jugadores: response.equipoBorrador.jugadores,
                importeTotal: response.equipoBorrador.importeTotal,
              });
          }
        }
      }
    } else {
      toast.show(response.mensaje || TEXTS.defaultError);
    }
  };

  const getTotalBudge = (players: TemplatePlayer[]): number => {
    const total = players.reduce((partialSum, player) => {
      return player.id !== undefined ? partialSum + player.cot : 0;
    }, 0);
    return total * 10000;
  };

  const handleSubmitNewTeamRecommender = async () => {
    if (getTotalBudge(playersWillBeAdded) > stateRecommend.budget) {
      toast.show(TEXTS.noBudget);
      return false;
    }

    if (playersWillBeAdded.length !== 0) {
      const arrayCoincidences: TemplatePlayer[] = [];
      for (let i = 0; i < playersWillBeAdded.length; i++) {
        const playerToAdd = playersWillBeAdded[i];
        for (let j = 0; j < stateRecommend.playersDrafter.length; j++) {
          const playersDraft = stateRecommend.playersDrafter[j];
          if (playerToAdd.id === playersDraft.id) {
            arrayCoincidences.push(playerToAdd);
          }
        }
      }
      if (arrayCoincidences.length !== 0) {
        if (arrayCoincidences.length === 1) {
          toast.show(
            `El jugador ${
              arrayCoincidences[0].a + ' ' + arrayCoincidences[0].n
            } ya forma parte de tu equipo, elegí otro.`
          );
        } else {
          toast.show(
            `Los jugadores ${arrayCoincidences
              .map((p) => {
                return p.a + ' ' + p.n;
              })
              .join(', ')} ya forman parte de tu equipo, elegí otros.`
          );
        }
      } else {
        handleAddPlayer([...playersWillBeAdded]);
      }
    } else {
      toast.show(
        'Seleccioná el o los jugadores recomendados para agregar a tu equipo.'
      );
    }
  };

  const contentWizard: ReactElement[] = [
    <motion.div
      key={stateRecommend.step}
      animate={'show'}
      initial="hide"
      variants={FADE_IN_OUT_LEFT}
    >
      <div className="text-gdt-gray mx-6">
        <h3 className="lg:text-black lg:text-center text-base font-bold text-gdt-gray">
          ¿Qué estás necesitando?
        </h3>
        <p className="lg:text-center lg:text-sm text-xs font-medium mt-2">
          El recomendador te ayuda a completar tu equipo.
        </p>
        <div className="space-y-4 lg:space-y-1 mt-6 lg:mt-4">
          {Object.keys(POSITIONS_LABELS.spanish).map((p, i) => {
            return (
              <Button
                key={p}
                className="w-full text-left lg:text-center bg-white capitalize lg:h-[3.25rem]"
                type="button"
                variant="secondary"
                onClick={() =>
                  setStateRecommend({
                    ...stateRecommend,
                    playerPosition: i + 1,
                    step: stateRecommend.step + 1,
                  })
                }
              >
                {p}
              </Button>
            );
          })}
        </div>
      </div>
    </motion.div>,
    <motion.div
      key={stateRecommend.step}
      animate={'show'}
      initial="hide"
      variants={FADE_IN_OUT_LEFT}
    >
      <div className="text-gdt-gray mx-6">
        <h3 className="text-base font-bold text-gdt-gray lg:text-black lg:text-center lg:mt-4">
          ¿Qué tipo de {getNamePositionById(stateRecommend.playerPosition)}{' '}
          buscás?
        </h3>
        <div className="space-y-4 lg:space-y-1 mt-6 lg:mt-8">
          {getCriteriaByPosition()?.map((p, index) => {
            return (
              <Button
                key={index}
                className="w-full text-left lg:text-center bg-white lg:h-[3.25rem]"
                type="button"
                variant="secondary"
                onClick={() => {
                  setStateRecommend({
                    ...stateRecommend,
                    criteria: +Object.keys(p) - 1,
                    step: stateRecommend.step + 1,
                  });
                }}
              >
                {Object.values(p)}
              </Button>
            );
          })}
        </div>
      </div>
    </motion.div>,
    <motion.div
      key={stateRecommend.step}
      animate={'show'}
      initial="hide"
      variants={FADE_IN_OUT_LEFT}
    >
      <div className="flex flex-col">
        <p className="text-sm font-medium text-gdt-gray-2 px-4 lg:hidden">
          Estos son los {getNamePositionById(stateRecommend.playerPosition)} que
          te recomendamos para completar tu equipo.
        </p>
        <h3 className="text-base w-full font-bold text-gdt-gray lg:text-black lg:text-center lg:mt-2 hidden lg:block">
          <span className="capitalize">
            {getNamePositionById(stateRecommend.playerPosition)}
          </span>{' '}
          recomendados
        </h3>
        <div className="flex justify-between  items-center pl-[20px] lg:pl-[2.5rem] pr-[15px] lg:pr-10 py-[16px]">
          <p className="text-sm text-gdt-blue-dark font-bold lg:text-gdt-gray-2 lg:text-xs">
            Titulares
          </p>
          <div className="flex gap-[6px]">
            <div className="w-[50px] lg:w-[70px] cursor-pointer">
              <p className="text-3xs text-gdt-gray-4 font-medium text-center">
                <span
                  className="flex justify-center items-center"
                  role="button"
                >
                  {isDesktop ? 'Equipos GDT' : 'Equipos \n GDT'}
                </span>
              </p>
            </div>
            <div className="w-[50px] lg:w-[80px] cursor-pointer">
              <p className="text-3xs text-gdt-gray-4 font-medium text-center">
                <span className="flex justify-end items-center" role="button">
                  {isDesktop ? 'Puntos Prom.' : 'Ptos. \n Prom.'}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="lg:overflow-y-auto lg:max-h-[43vh]">
          <div className="space-y-[.1rem] lg:px-6">
            {!Array.isArray(stateRecommend.headlines)
              ? renderPlayerOnList('headlines', stateRecommend.headlines)
              : stateRecommend.headlines?.map((player) => {
                  {
                    return renderPlayerOnList('headlines', player);
                  }
                })}
          </div>
          <p className="pl-[20px] pb-[7px] pt-6 lg:pl-[24px] lg:pb-[10px] lg:pt-2 text-sm text-gdt-blue-dark font-bold lg:text-gdt-gray-2 lg:text-xs">
            Suplente
          </p>
          <div className="space-y-[.1rem] lg:px-6">
            {renderPlayerOnList('substitutes', stateRecommend.substitute)}
          </div>
        </div>
        <div className="flex-1 py-5 lg:pt-5 lg:pb-0 lg:px-6 text-center px-6">
          <Button
            className="w-full lg:max-w-xs"
            type="button"
            variant="primary"
            onClick={() => handleSubmitNewTeamRecommender()}
          >
            Agregar a mi equipo
          </Button>
        </div>
      </div>
    </motion.div>,
  ];

  return (
    <>
      {
        <Loader
          isVisible={isLoading || isLoadingDraftOnSubmit}
          timeout={isLoadingDraftOnSubmit}
        />
      }
      {isDesktop ? (
        <StepWizard
          dotsPosition="top"
          getCurrentStep={(activeStep: number) =>
            setStateRecommend({ ...stateRecommend, step: activeStep })
          }
          isControlled={true}
          stepControlled={stateRecommend.step}
          onSubmit={() => {
            return null;
          }}
        >
          {contentWizard}
        </StepWizard>
      ) : (
        <>
          <NavBar
            mobile={{
              leftContent: stateRecommend.step !== 0 && {
                backAction: () => {
                  setStateRecommend({
                    ...stateRecommend,
                    step: stateRecommend.step - 1,
                  });
                },
              },
              centerContent: {
                title,
              },
              rightContent: {
                closeMenuActionUrl: ROUTES.buildTeam11,
              },
            }}
          >
            <div className="mt-4">
              <StepWizard
                dotsPosition="top"
                isControlled={true}
                stepControlled={stateRecommend.step}
                onSubmit={() => {
                  alert();
                }}
              >
                {contentWizard}
              </StepWizard>
            </div>
          </NavBar>
        </>
      )}
    </>
  );
};
